import { useState, useEffect } from "react";
import {
    Box,
    Card,
    CardContent,
    Grid,
    TextField,
    Divider,
    Radio,
    RadioGroup,
    FormControl,
    FormControlLabel,
    Typography,
    CardActions,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useData } from "../../../context/DataContext";
import { createEmployee } from "../../../context/createAPI";
import SingleSelect from "../../../components/Selections/singleSelect";
import MultiSelectWithAll from "../../../components/Selections/multiSelectWithAll";
import SnackbarMessage from "../../../components/snackBar/snackBar";
import CustomButton from "../../../components/buttons/customButton";

/* ------------------ CONSTANTS ------------------ */
const genders = [
    { value: "1", label: "Male" },
    { value: "2", label: "Female" },
    { value: "3", label: "Others" },
];


/* ------------------ COMPONENT ------------------ */

function EmployeeCreation() {
    const { state: locationState } = useLocation();
    const navigate = useNavigate();
    const { employeesData } = locationState || {};
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

    const {
        employees = [],
        levels = [],
        user = {},
        modulePages = [],
        handleEmployees,
    } = useData();

    const [state, setState] = useState({
        selectedRadioOption: 1,
        employeeName: "",
        employeeMobile: "",
        gender: "1",
        employeeEducation: "",
        employeeExperience: "",
        headEmployee: "",
        headEmployeesArray: [],
        selectedModules: [],
        level: "",
        password: "",
        loading: false,
        submitMssg: "",
    });
    const [errors, setErrors] = useState({});

    /* ------------------ HELPERS ------------------ */

    const getHeadEmployeesByLevel = (level) =>
        employees
            .find(item => item.level_id === level - 1)
            ?.data?.map(emp => ({
                value: emp.user_id,
                label: emp.user_name,
            })) || [];

    /* ------------------ EFFECT ------------------ */

    useEffect(() => {
        if (employeesData?.length) {
            const level = user.user_level_id + 1;

            setState(prev => ({
                ...prev,
                selectedRadioOption: level,
                headEmployeesArray: getHeadEmployeesByLevel(level),
            }));
        }
    }, [employeesData, employees, user.user_level_id]);

    /* ------------------ HANDLERS ------------------ */


    const handleInputChange = (name, value) => {
        setErrors(prev => ({ ...prev, [name]: "" }));

        if (name === "selectedRadioOption") {
            setState(prev => ({
                ...prev,
                selectedRadioOption: value,
                headEmployeesArray: getHeadEmployeesByLevel(value),
            }));
            return;
        }

        if (name === "headEmployee") {
            setState(prev => ({
                ...prev,
                headEmployee: value,
                level: "",
                selectedModules: [],
            }));
            return;
        }

        if (name === "level") {
            setState(prev => ({
                ...prev,
                level: value,
                selectedModules: [],
            }));
            return;
        }

        setState(prev => ({ ...prev, [name]: value }));
    };

    const handleMultiselectChange = (name, values) => {
        setState(prev => ({ ...prev, [name]: values }));
        setErrors(prev => ({ ...prev, [name]: "" }));
    };

    /* ------------------ VALIDATION ------------------ */

    const validateForm = () => {
        const err = {};
        if (!state.employeeName) err.employeeName = "Enter Name";
        if (!state.password) err.password = "Enter Password";
        if (!state.employeeMobile || state.employeeMobile.length !== 10)
            err.employeeMobile = "Mobile must be 10 digits";
        if (!state.gender) err.gender = "Select Gender";
        if (!state.employeeEducation) err.employeeEducation = "Enter Education";
        if (!state.employeeExperience) err.employeeExperience = "Enter Experience";
        if (!state.headEmployee) err.headEmployee = "Select Head Employee";
        if ([3, 4].includes(state.selectedRadioOption) && !state.level)
            err.level = "Select Level";
        if (!state.selectedModules.length)
            err.selectedModules = "Select Modules";
        return err;
    };

    /* ------------------ SUBMIT ------------------ */

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        const err = validateForm();

        if (Object.keys(err).length) {
            setErrors(err);
            return;
        }

        try {
            setState(prev => ({ ...prev, loading: true }));

            const res = await createEmployee({
                user_level_id: state.selectedRadioOption,
                level_id: state.level || null,
                name: state.employeeName,
                mobile_no: state.employeeMobile,
                password: state.password,
                module_pages: modulePages.filter(m =>
                    state.selectedModules.map(x => x.value).includes(m.value)
                ),
                gender: state.gender,
                education: state.employeeEducation,
                experience: state.employeeExperience,
                client_id: parseInt(localStorage.getItem("user_client_id")),
                client_user_head_id: state.headEmployee,
                inserted_by: user.user_id,
            });

            if (res.statusCode === 200) {
                handleEmployees();
                navigate("/employee/history");
                window.location.reload();

                setSnackbar({
                    open: true,
                    message: res.message || "Employee Created Successfully",
                    severity: "success",
                });
            }
        } catch (err) {
            console.error(err);
            setSnackbar({
                open: true,
                message: err,
                severity: "success",
            });
        } finally {
            setState(prev => ({ ...prev, loading: false }));
        }
    };

    /* ------------------ RENDER ------------------ */
    const handleChange = (name, value) => {
        let newValue = value;
        setErrors(prev => ({ ...prev, [name]: "" }));
        if (name === "password") {
            newValue = value.replace(/^\s+/, "").slice(0, 6);
        } else if (name === "employeeMobile") {
            newValue = value.replace(/\D/g, "").slice(0, 10);
        } else if (name === "employeeEducation") {
            newValue.slice(0, 10);
        } else if (name === "employeeExperience") {
            newValue.slice(0, 2);
        } else if (name === "employeeName") {
            newValue.slice(0, 50);
        }

        setState((prev) => ({
            ...prev,
            [name]: newValue,
        }));
    };


    return (
        <Box>
            <Card elevation={6}>
                <CardContent>
                    <Divider sx={{ mb: 2 }} />

                    {/* USER LEVEL RADIO */}
                    <FormControl>
                        <RadioGroup
                            row
                            value={state.selectedRadioOption}
                            onChange={(e) =>
                                handleInputChange(
                                    "selectedRadioOption",
                                    Number(e.target.value)
                                )
                            }
                        >
                            {employeesData?.map(opt => (
                                <FormControlLabel
                                    key={opt.level_id}
                                    value={opt.level_id}
                                    control={<Radio size="small" color="success" />}
                                    label={opt.user_level_name}
                                />
                            ))}
                        </RadioGroup>
                    </FormControl>

                    {/* TEXT INPUTS */}
                    <Grid container spacing={2} mt={1}>
                        {[
                            { label: "Employee Name", name: "employeeName" },
                            { label: "Password", name: "password" },
                            { label: "Mobile", name: "employeeMobile" },
                            { label: "Education", name: "employeeEducation" },
                            { label: "Experience", name: "employeeExperience" },
                        ].map(f => (
                            <Grid item size={{ xs: 12, md: 4 }} key={f.name}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label={f.label}
                                    value={state[f.name]}
                                    onChange={(e) => handleChange(f.name, e.target.value)}
                                    inputProps={{
                                        ...(f.name === "employeeName" && { maxLength: 50 }),
                                        ...(f.name === "employeeMobile" && { inputMode: "numeric", pattern: "[0-9]*", maxLength: 10 }),
                                        ...(f.name === "password" && { maxLength: 6 }),
                                        ...(f.name === "employeeEducation" && { maxLength: 10 }),
                                        ...(f.name === "employeeExperience" && { maxLength: 2 }),
                                    }}
                                    error={Boolean(errors[f.name])}
                                    helperText={errors[f.name]}
                                />
                            </Grid>
                        ))}

                        {/* GENDER */}
                        <Grid item size={{ xs: 12, md: 4 }}>
                            <Typography fontWeight="bold">Gender</Typography>
                            <RadioGroup
                                row
                                value={state.gender}
                                onChange={(e) =>
                                    handleInputChange(
                                        "gender",
                                        Number(e.target.value)
                                    )
                                }
                            >
                                {genders.map(g => (
                                    <FormControlLabel
                                        key={g.value}
                                        value={g.value}
                                        control={<Radio size="small" color="success" />}
                                        label={g.label}
                                    />
                                ))}
                            </RadioGroup>
                        </Grid>

                        {/* SELECTS */}
                        {[
                            {
                                type: "single",
                                name: "headEmployee",
                                label: "Head Employee",
                                options: state.headEmployeesArray,
                                error: errors.headEmployee,
                                value: state.headEmployee || "",
                            },
                            ...(state.selectedRadioOption !== 2
                                ? [{
                                    type: "single",
                                    name: "level",
                                    label: "Levels",
                                    options: levels,
                                    error: errors.level,
                                    value: state.level || "",
                                }]
                                : []),
                            {
                                type: "multi",
                                name: "selectedModules",
                                label: "Select Modules",
                                error: errors.selectedModules,
                                options: modulePages.map(m => ({
                                    value: m.value,
                                    label: m.label,
                                })),
                                value: state.selectedModules,
                            },
                        ].map(field => (
                            <Grid item size={{ xs: 12, md: 3 }} key={field.name}>
                                {field.type === "single" ? (
                                    <SingleSelect
                                        {...field}
                                        error={Boolean(errors[field.name])}
                                        helperText={errors[field.name] || ""}
                                        onChange={handleInputChange}
                                    />
                                ) : (
                                    <MultiSelectWithAll
                                        {...field}
                                        error={Boolean(errors[field.name])}
                                        helperText={errors[field.name] || ""}
                                        onChange={handleMultiselectChange}
                                    />
                                )}
                            </Grid>
                        ))}
                    </Grid>
                </CardContent>

                <CardActions sx={{ justifyContent: "center", mb: 1, mt: 1 }}>
                    <CustomButton
                        status={state.loading === true ? "loading" : "idle"}
                        onClick={handleFormSubmit}
                        label="Add Employee"
                        loadingLabel="Submitting.."
                    />
                </CardActions>
            </Card>

            <SnackbarMessage
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
            />
        </Box>
    );
}
export default EmployeeCreation;